/**
 * Main Chess Game Component
 * 
 * Integrates all chess functionality including:
 * - Game mode selection
 * - Single-player with AI
 * - Multiplayer (real-time and turn-based)
 * - Game persistence
 * - Statistics tracking
 * - Error handling
 * - Responsive design
 * - Dark mode support
 * - Accessibility features
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ChessEngine } from '../ChessEngine';
import { ChessAI } from '../ChessAI';
import { ChessBoard } from './ChessBoard';
import { MoveHistory } from './MoveHistory';
import { CapturedPieces } from './CapturedPieces';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { PromotionDialog } from './PromotionDialog';
import { GameCoach } from '../../components/GameCoach';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { GameCoachService } from '../../services/GameCoachService';
import { chessKnowledge } from '../../services/gameCoachKnowledge';
import { ChessCoachAI, MoveExplanation } from '../services/ChessCoachAI';
import { MultiplayerChessService } from '../services/MultiplayerChessService';
import { TurnBasedChessService } from '../services/TurnBasedChessService';
import { ChessGamePersistence } from '../services/ChessGamePersistence';
import { ChessStatisticsService } from '../services/ChessStatisticsService';
import { ChessErrorHandler } from '../services/ChessErrorHandler';
import { GameMode, AIDifficulty, Position, PieceType, PlayerColor, Move, TimeControl, GameSettings } from '../types';
import { ProfileManager } from '../../services/ProfileManager';
import { StatisticsManager } from '../../services/StatisticsManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { NotificationService } from '../../services/NotificationService';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { WebSocketService } from '../../services/WebSocketService';
import { useAuthentication } from '../../hooks/useAuthentication';
import './ChessGame.css';

interface ChessGameProps {
  onExit?: () => void;
  initialRoomCode?: string; // Room code from deep link
}

export const ChessGame: React.FC<ChessGameProps> = ({ onExit, initialRoomCode }) => {
  // Core game state
  const [engine, setEngine] = useState(() => new ChessEngine());
  const [gameMode, setGameMode] = useState<GameMode | null>(null);
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('intermediate');
  const [ai] = useState(() => new ChessAI('intermediate'));
  const [playerColor, setPlayerColor] = useState<PlayerColor>('white');
  const [moveCount, setMoveCount] = useState(0); // Track moves to force re-renders
  const [, setIsEngineReady] = useState(false); // Track engine initialization
  const [aiMoveDelay, setAiMoveDelay] = useState(1000); // AI move delay in milliseconds (default 1 second)
  
  // Multiplayer state
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [, setOpponentConnected] = useState(false);
  
  // Ref to prevent multiple join attempts
  const hasAttemptedJoin = React.useRef(false);
  
  // Ref to prevent multiple room creation attempts
  const isCreatingRoom = React.useRef(false);
  
  // UI state
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [showPromotion, setShowPromotion] = useState(false);
  const [promotionMove, setPromotionMove] = useState<{ from: Position; to: Position } | null>(null);
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [isAIThinking, setIsAIThinking] = useState(false);
  
  // AI Coach state
  const [coachAI] = useState(() => new ChessCoachAI('AIzaSyABjLQr0AmUKnlh_6fizMQ__s_WR7I2QHc'));
  const [coachService] = useState(() => new GameCoachService('Chess', chessKnowledge));
  const [lastMoveExplanation, setLastMoveExplanation] = useState<MoveExplanation | undefined>();
  
  // Services
  const { user, isAuthenticated } = useAuthentication();
  const [services] = useState(() => {
    const storage = new LocalStorageManager();
    const notifications = NotificationService.getInstance();
    const profileManager = new ProfileManager();
    const statsManager = new StatisticsManager(profileManager);
    const errorService = ErrorHandlingService.getInstance();
    const wsService = WebSocketService.getInstance();
    
    return {
      multiplayer: new MultiplayerChessService(wsService, engine),
      turnBased: new TurnBasedChessService(storage, notifications),
      persistence: new ChessGamePersistence(storage),
      statistics: new ChessStatisticsService(statsManager),
      errorHandler: new ChessErrorHandler(errorService, notifications)
    };
  });

  // Multiplayer connection status
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');

  // Setup multiplayer event handlers
  useEffect(() => {
    // Game started handler
    services.multiplayer.onGameStarted(() => {
      console.log('♟️ Chess game started');
      setWaitingForOpponent(false);
      setOpponentConnected(true);
      // Don't reinitialize engine - it's already initialized
      // engine.initializeGame(); // REMOVED - causes issues
      setIsEngineReady(true);
    });

    // Move update handler
    services.multiplayer.onMoveUpdate((move, gameState) => {
      console.log('♟️ Received move update:', move);
      
      // CRITICAL: Check if this move was made by the opponent
      // If the current turn in gameState matches our color, it means opponent just moved
      // and we should apply the move. Otherwise, skip (we already applied it locally).
      const currentTurnAfterMove = gameState.currentTurn;
      const myColor = services.multiplayer.getPlayerColor();
      
      console.log('🎮 Move update check:', {
        currentTurnAfterMove,
        myColor,
        shouldApply: currentTurnAfterMove === myColor
      });
      
      // Only apply if it's now our turn (meaning opponent just moved)
      if (currentTurnAfterMove === myColor) {
        // Update engine with server game state
        // Convert server format {row, col} to Position {rank, file}
        // row 0 = rank 7 (top of board)
        // row 7 = rank 0 (bottom of board)
        const from: Position = { rank: 7 - move.from.row, file: move.from.col };
        const to: Position = { rank: 7 - move.to.row, file: move.to.col };
        
        console.log('✅ Applying opponent move:', {
          serverMove: move,
          convertedFrom: from,
          convertedTo: to
        });
        engine.makeMove(from, to, move.promotion);
        setMoveCount(prev => prev + 1);
      } else {
        console.log('⏭️ Skipping own move (already applied locally)');
      }
    });

    // Game over handler
    services.multiplayer.onGameOver((result, winner) => {
      console.log('♟️ Game over:', result, 'Winner:', winner);
      NotificationService.getInstance().sendInfoNotification(
        result === 'checkmate' ? `Checkmate! ${winner} wins!` :
        result === 'stalemate' ? 'Stalemate! Game is a draw.' :
        result === 'draw' ? 'Game ended in a draw.' :
        result === 'resignation' ? `${winner} wins by resignation!` :
        'Game over'
      );
      handleGameEnd();
    });

    // Opponent disconnected handler
    services.multiplayer.onOpponentDisconnected(() => {
      console.log('♟️ Opponent disconnected');
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification('Opponent disconnected. Waiting for reconnection...');
    });

    // Opponent reconnected handler
    services.multiplayer.onOpponentReconnected(() => {
      console.log('♟️ Opponent reconnected');
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    // Game state received handler (for reconnection)
    services.multiplayer.onGameStateReceived((gameState) => {
      console.log('♟️ Received game state:', gameState);
      // Sync engine with server state
      // This would require a method to set the full game state
      setMoveCount(prev => prev + 1);
    });

    // Error handler
    services.multiplayer.onError((error) => {
      console.error('♟️ Multiplayer error:', error);
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });

    // Connection status handler
    services.multiplayer.onConnectionStatus((status) => {
      console.log('♟️ Connection status:', status);
      setConnectionStatus(status);
      
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });
  }, [services.multiplayer, engine]);

  // Handle deep link room code
  useEffect(() => {
    // CRITICAL: Only run if we have an initialRoomCode from props (deep link)
    // Do NOT run if roomCode is set via manual join
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;
      console.log('♟️ Chess deep link detected, joining room:', initialRoomCode);
      
      // Set up multiplayer mode and join the room
      setGameMode('multiplayer-realtime');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);
      setShowModeSelector(false);
      
      // Attempt to join the room
      services.multiplayer.joinRoom(initialRoomCode)
        .then(() => {
          console.log('✅ Successfully joined chess room via deep link');
          const color = services.multiplayer.getPlayerColor();
          if (color) {
            setPlayerColor(color);
          }
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error) => {
          console.error('❌ Failed to join chess room:', error);
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setShowModeSelector(true);
          setWaitingForOpponent(false);
          setRoomCode(null);
          // DON'T reset hasAttemptedJoin - prevent infinite retry loop
        });
    }
  }, [initialRoomCode]); // CRITICAL: Only depend on initialRoomCode, NOT roomCode

  // Initialize game only when mode is selected
  useEffect(() => {
    // Don't initialize until mode is selected
    if (!gameMode) return;
    
    console.log('ChessGame: Initializing engine for mode:', gameMode, 'difficulty:', aiDifficulty);
    
    // ALWAYS start with a fresh game - don't load auto-save
    engine.initializeGame();
    setIsEngineReady(true);
    setHasInitialized(true); // Mark as initialized
    console.log('ChessGame: Engine initialized with fresh board');
    
    // Debug: Check move history immediately after initialization
    const gameState = engine.getGameState();
    console.log('ChessGame: Move history length after init:', gameState.moveHistory.length);
    console.log('ChessGame: Move history:', gameState.moveHistory);
    
    // Force a re-render to show pieces
    setMoveCount(prev => prev + 1);
  }, [gameMode, aiDifficulty, engine]);

  // Auto-save on state change (but don't save on initial render)
  const [hasInitialized, setHasInitialized] = useState(false);
  
  useEffect(() => {
    if (gameMode && !showModeSelector && hasInitialized) {
      const gameState = engine.getGameState();
      // Only auto-save if there are actual moves (not just initialization)
      if (gameState.moveHistory.length > 0) {
        services.persistence.autoSave(gameMode, gameState, {
          players: {
            white: isAuthenticated ? user?.name || 'Player' : 'Guest',
            black: gameMode === 'singleplayer' ? 'AI' : 'Opponent'
          },
          aiDifficulty: gameMode === 'singleplayer' ? aiDifficulty : undefined,
          startedAt: Date.now(),
          lastSavedAt: Date.now()
        });
      }
    }
  }, [moveCount, gameMode, showModeSelector, services.persistence, isAuthenticated, user, aiDifficulty, hasInitialized, engine]);

  // Handle square click
  const handleSquareClick = useCallback((position: Position) => {
    // In multiplayer, check if it's our turn
    if (gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased') {
      const currentTurn = engine.getCurrentPlayer();
      const myColor = services.multiplayer.getPlayerColor();
      console.log('🎮 Turn check:', {
        currentTurn,
        myColor,
        isMyTurn: services.multiplayer.isMyTurn(currentTurn)
      });
      
      if (!services.multiplayer.isMyTurn(currentTurn)) {
        NotificationService.getInstance().sendWarningNotification("It's not your turn!");
        return;
      }
    }

    if (gameMode === 'singleplayer' && engine.getCurrentPlayer() !== playerColor) {
      // It's AI's turn, ignore clicks
      return;
    }

    if (!selectedSquare) {
      // Select piece
      const piece = engine.getBoard().getPieceAt(position);
      if (piece && piece.color === engine.getCurrentPlayer()) {
        setSelectedSquare(position);
      }
    } else {
      // Try to move
      const legalMoves = engine.getLegalMoves(selectedSquare);
      const isLegalMove = legalMoves.some(
        move => move.rank === position.rank && move.file === position.file
      );

      if (isLegalMove) {
        // Check if promotion is needed
        const piece = engine.getBoard().getPieceAt(selectedSquare);
        if (piece?.type === 'pawn' && 
            ((piece.color === 'white' && position.rank === 7) ||
             (piece.color === 'black' && position.rank === 0))) {
          setPromotionMove({ from: selectedSquare, to: position });
          setShowPromotion(true);
        } else {
          executeMove(selectedSquare, position);
        }
      }
      
      setSelectedSquare(null);
    }
  }, [selectedSquare, engine, gameMode, playerColor, services.multiplayer]);

  // Execute move
  const executeMove = useCallback((from: Position, to: Position, promotion?: 'queen' | 'rook' | 'bishop' | 'knight') => {
    const result = engine.makeMove(from, to, promotion);
    
    if (!result.success) {
      services.errorHandler.handleInvalidMove(from, to, result.error || 'Unknown error');
      return;
    }

    // Send move to multiplayer service if in multiplayer mode
    if (gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased') {
      const gameState = engine.getGameState();
      
      // Convert GameState to the format expected by Lambda
      const chessGameState = {
        board: (gameState.board as any).map((row: any[]) => row.map((piece: any) => piece ? `${piece.color[0]}${piece.type[0]}` : '')),
        currentTurn: gameState.currentPlayer,
        moveHistory: gameState.moveHistory,
        capturedPieces: {
          white: gameState.capturedPieces.white.map(p => `${p.color[0]}${p.type[0]}`),
          black: gameState.capturedPieces.black.map(p => `${p.color[0]}${p.type[0]}`)
        },
        castlingRights: {
          whiteKingSide: gameState.castlingRights.whiteKingside,
          whiteQueenSide: gameState.castlingRights.whiteQueenside,
          blackKingSide: gameState.castlingRights.blackKingside,
          blackQueenSide: gameState.castlingRights.blackQueenside
        },
        enPassantTarget: gameState.enPassantSquare ? { row: 7 - gameState.enPassantSquare.rank, col: gameState.enPassantSquare.file } : null,
        halfMoveClock: gameState.halfMoveClock,
        fullMoveNumber: gameState.fullMoveNumber,
        isCheck: gameState.gameStatus.type === 'check',
        isCheckmate: gameState.gameStatus.type === 'checkmate',
        isStalemate: gameState.gameStatus.type === 'stalemate',
        isDraw: gameState.gameStatus.type === 'draw'
      };
      
      services.multiplayer.sendMove(from, to, promotion as PieceType | undefined, chessGameState);
    }

    // Check if game ended
    const gameState = engine.getGameState();
    if (gameState.gameStatus.type !== 'active' && gameState.gameStatus.type !== 'check') {
      handleGameEnd();
    }

    // AI move in single-player
    if (gameMode === 'singleplayer' && engine.getCurrentPlayer() !== playerColor) {
      setTimeout(() => {
        const aiMove = ai.calculateBestMove(engine, 5000);
        if (aiMove) {
          const aiPromotion = aiMove.promotion as 'queen' | 'rook' | 'bishop' | 'knight' | undefined;
          engine.makeMove(aiMove.from, aiMove.to, aiPromotion);
          
          // Check if game ended after AI move
          const newGameState = engine.getGameState();
          if (newGameState.gameStatus.type !== 'active' && newGameState.gameStatus.type !== 'check') {
            handleGameEnd();
          }
        }
      }, 500);
    }
  }, [engine, gameMode, playerColor, ai, services.errorHandler, services.multiplayer]);

  // Handle promotion selection
  const handlePromotionSelect = useCallback((pieceType: 'queen' | 'rook' | 'bishop' | 'knight') => {
    if (promotionMove) {
      executeMove(promotionMove.from, promotionMove.to, pieceType);
      setShowPromotion(false);
      setPromotionMove(null);
    }
  }, [promotionMove, executeMove]);

  // Handle move made (called by ChessBoard after each move)
  const handleMoveMade = useCallback((move: Move) => {
    console.log('ChessGame: Move made:', move);
    
    // CRITICAL: Send move to multiplayer service if in multiplayer mode
    if (gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased') {
      console.log('📤 Sending move to multiplayer service:', move);
      
      const gameState = engine.getGameState();
      
      // Convert GameState to the format expected by Lambda
      const chessGameState = {
        board: (gameState.board as any).map((row: any[]) => row.map((piece: any) => piece ? `${piece.color[0]}${piece.type[0]}` : '')),
        currentTurn: gameState.currentPlayer,
        moveHistory: gameState.moveHistory,
        capturedPieces: {
          white: gameState.capturedPieces.white.map(p => `${p.color[0]}${p.type[0]}`),
          black: gameState.capturedPieces.black.map(p => `${p.color[0]}${p.type[0]}`)
        },
        castlingRights: {
          whiteKingSide: gameState.castlingRights.whiteKingside,
          whiteQueenSide: gameState.castlingRights.whiteQueenside,
          blackKingSide: gameState.castlingRights.blackKingside,
          blackQueenSide: gameState.castlingRights.blackQueenside
        },
        enPassantTarget: gameState.enPassantSquare ? { row: 7 - gameState.enPassantSquare.rank, col: gameState.enPassantSquare.file } : null,
        halfMoveClock: gameState.halfMoveClock,
        fullMoveNumber: gameState.fullMoveNumber,
        isCheck: gameState.gameStatus.type === 'check',
        isCheckmate: gameState.gameStatus.type === 'checkmate',
        isStalemate: gameState.gameStatus.type === 'stalemate',
        isDraw: gameState.gameStatus.type === 'draw'
      };
      
      services.multiplayer.sendMove(
        move.from,
        move.to,
        move.promotion as PieceType | undefined,
        chessGameState
      );
    }
    
    // Force re-render to update UI
    setMoveCount(prev => prev + 1);
    
    // Check if game ended
    const gameState = engine.getGameState();
    if (gameState.gameStatus.type !== 'active' && gameState.gameStatus.type !== 'check') {
      console.log('ChessGame: Game ended:', gameState.gameStatus);
      return;
    }
    
    // Trigger AI move if it's single-player and AI's turn
    if (gameMode === 'singleplayer') {
      const currentPlayer = engine.getCurrentPlayer();
      console.log('ChessGame: Current player after move:', currentPlayer, 'Player color:', playerColor);
      
      if (currentPlayer !== playerColor) {
        console.log('ChessGame: AI turn - will calculate move after short delay');
        
        // Use a longer delay to ensure board state is fully updated
        setTimeout(() => {
          makeAIMove();
        }, 100);
      }
    }
  }, [gameMode, playerColor, engine, services.multiplayer]);

  // Separate function to make AI move
  const makeAIMove = useCallback(() => {
    console.log('ChessGame: Making AI move...');
    
    // Double-check it's still AI's turn
    const currentPlayer = engine.getCurrentPlayer();
    if (currentPlayer === playerColor) {
      console.log('ChessGame: Not AI turn anymore, skipping');
      return;
    }
    
    console.log('ChessGame: Calculating AI move for', currentPlayer);
    
    // Show AI thinking indicator
    setIsAIThinking(true);
    
    try {
      const aiMove = ai.calculateBestMove(engine, 5000);
      console.log('ChessGame: AI calculated move:', aiMove);
      
      if (!aiMove) {
        console.warn('ChessGame: AI could not find a move');
        setIsAIThinking(false);
        return;
      }
      
      // Validate move
      if (aiMove.from.file === aiMove.to.file && aiMove.from.rank === aiMove.to.rank) {
        console.error('ChessGame: AI returned invalid move - same position');
        setIsAIThinking(false);
        return;
      }
      
      // Add a delay to show the AI is "thinking" (makes it feel more natural)
      setTimeout(async () => {
        // Execute AI move
        const aiPromotion = aiMove.promotion as 'queen' | 'rook' | 'bishop' | 'knight' | undefined;
        const result = engine.makeMove(aiMove.from, aiMove.to, aiPromotion);
        
        console.log('ChessGame: AI move result:', result);
        
        if (result.success && result.move) {
          // Get AI explanation for the move
          if (gameMode === 'singleplayer' && coachAI) {
            try {
              const explanation = await coachAI.explainMove(
                result.move,
                engine.getGameState(),
                playerColor
              );
              setLastMoveExplanation(explanation);
            } catch (error) {
              console.error('ChessGame: Error getting move explanation:', error);
            }
          }
          
          // Force re-render to show AI move
          setMoveCount(prev => prev + 1);
          
          // Check if game ended
          const newGameState = engine.getGameState();
          if (newGameState.gameStatus.type !== 'active' && newGameState.gameStatus.type !== 'check') {
            console.log('ChessGame: Game ended after AI move:', newGameState.gameStatus);
          }
        } else {
          console.error('ChessGame: AI move failed:', result.error);
        }
        
        // Hide AI thinking indicator
        setIsAIThinking(false);
      }, aiMoveDelay); // Use configurable delay
      
    } catch (error) {
      console.error('ChessGame: Error during AI move:', error);
      setIsAIThinking(false);
    }
  }, [engine, ai, playerColor]);

  // Handle game end
  const handleGameEnd = useCallback(() => {
    const gameState = engine.getGameState();
    const status = gameState.gameStatus;
    
    // Record statistics
    if (status.type === 'checkmate' || status.type === 'resignation') {
      const winner = status.winner;
      services.statistics.recordGameResult({
        gameId: `game_${Date.now()}`,
        mode: gameMode!,
        difficulty: gameMode === 'singleplayer' ? aiDifficulty : undefined,
        winner,
        isDraw: false,
        reason: status.type,
        moveCount: gameState.moveHistory.length,
        duration: 0, // Would need to track actual duration
        completedAt: Date.now()
      }, playerColor, isAuthenticated ? user?.id : undefined);
    } else if (status.type === 'draw' || status.type === 'stalemate') {
      services.statistics.recordGameResult({
        gameId: `game_${Date.now()}`,
        mode: gameMode!,
        difficulty: gameMode === 'singleplayer' ? aiDifficulty : undefined,
        isDraw: true,
        reason: status.type,
        moveCount: gameState.moveHistory.length,
        duration: 0,
        completedAt: Date.now()
      }, playerColor, isAuthenticated ? user?.id : undefined);
    }
  }, [engine, gameMode, aiDifficulty, playerColor, isAuthenticated, user, services.statistics]);

  // Handle new game
  const handleNewGame = useCallback(() => {
    // Create a completely fresh engine instance
    setEngine(new ChessEngine());
    setSelectedSquare(null);
    setShowModeSelector(true);
    setGameMode(null); // Clear game mode
    setMoveCount(0); // Reset move count
    setHasInitialized(false); // Reset initialization flag
    
    // Reset multiplayer state
    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    
    // Leave multiplayer game if active
    if (roomCode) {
      services.multiplayer.leaveGame();
    }
    
    services.persistence.clearAutoSave();
  }, [services.persistence, services.multiplayer, roomCode]);

  // Handle undo
  const handleUndo = useCallback(() => {
    if (gameMode === 'singleplayer') {
      // Undo both player and AI moves
      engine.undoMove(2);
    } else {
      engine.undoMove(1);
    }
    // Force re-render to update the board
    setMoveCount(prev => prev + 1);
  }, [engine, gameMode]);

  // Handle asking coach a question (handled internally by GameCoach component)
  // Legacy handleAskCoach removed — GameCoach manages its own Q&A

  if (showModeSelector && !roomCode && !gameMode) {
    // Only show mode selector if we're not in a game
    const chessConfig: GameModeConfig = {
      gameName: 'Chess',
      gameIcon: '♟️',
      availableModes: ['ai', 'local', 'online'],
      difficulties: [
        { id: 'beginner', label: 'Beginner', icon: '🌱' },
        { id: 'intermediate', label: 'Intermediate', icon: '🎯' },
        { id: 'expert', label: 'Expert', icon: '👑' },
      ],
      extraSections: [{
        title: 'Time Control',
        icon: '⏱️',
        options: [
          { id: 'blitz-3', label: 'Blitz 3+2', icon: '⚡' },
          { id: 'blitz-5', label: 'Blitz 5+0', icon: '⚡' },
          { id: 'rapid-10', label: 'Rapid 10+0', icon: '🕐' },
          { id: 'rapid-15', label: 'Rapid 15+10', icon: '🕐' },
          { id: 'unlimited', label: 'Unlimited', icon: '♾️' },
        ],
        defaultSelected: 'unlimited',
        defaultOpen: false,
        visibleForModes: ['ai', 'online'],
      }],
    };

    const mapTimeControl = (id: string): TimeControl => {
      switch (id) {
        case 'blitz-3': return { type: 'blitz', initialTime: 180, increment: 2 };
        case 'blitz-5': return { type: 'blitz', initialTime: 300, increment: 0 };
        case 'rapid-10': return { type: 'rapid', initialTime: 600, increment: 0 };
        case 'rapid-15': return { type: 'rapid', initialTime: 900, increment: 10 };
        case 'unlimited':
        default: return { type: 'unlimited', initialTime: 0, increment: 0 };
      }
    };

    const handleModeSelection = async (selection: GameModeSelection) => {
      const timeControlId = selection.extras?.['extra-0'] || 'unlimited';
      const timeControl = mapTimeControl(timeControlId);

      if (selection.mode === 'ai' && selection.difficulty) {
        const settings: GameSettings = {
          mode: 'singleplayer',
          aiDifficulty: selection.difficulty as AIDifficulty,
          timeControl,
          isPrivate: false,
        };
        console.log('ChessGame: onStartGame called with settings:', settings);
        setGameMode(settings.mode);
        setAiDifficulty(settings.aiDifficulty!);
        ai.setDifficulty(settings.aiDifficulty!);
        setShowModeSelector(false);
        engine.initializeGame();
        setIsEngineReady(true);
      } else if (selection.mode === 'local') {
        const settings: GameSettings = {
          mode: 'singleplayer',
          timeControl,
          isPrivate: false,
        };
        setGameMode(settings.mode);
        setShowModeSelector(false);
        engine.initializeGame();
        setIsEngineReady(true);
      } else if (selection.mode === 'online') {
        // Handle multiplayer game creation
        if (isCreatingRoom.current) {
          console.warn('⚠️ Room creation already in progress, ignoring duplicate request');
          return;
        }

        isCreatingRoom.current = true;
        console.log('🔍 Starting room creation from ChessGame...');

        try {
          setGameMode('multiplayer-realtime');
          const room = await services.multiplayer.createRoom();
          setRoomCode(room.code);
          setWaitingForOpponent(true);
          const color = services.multiplayer.getPlayerColor();
          console.log('🎨 ChessGame: Setting player color after room creation:', color);
          if (color) {
            setPlayerColor(color);
          }
          setShowModeSelector(false);

          console.log('✅ Chess room created:', room.code);
          isCreatingRoom.current = false;
        } catch (error: any) {
          console.error('Failed to create multiplayer game:', error);
          NotificationService.getInstance().sendErrorNotification(
            `Failed to create multiplayer game: ${error.message || 'Unknown error'}`
          );
          setWaitingForOpponent(false);
          isCreatingRoom.current = false;
        }
      }
    };

    const handleJoinPrivateGame = async (code: string) => {
      console.log('ChessGame: onJoinPrivateGame called with roomCode:', code);

      setGameMode('multiplayer-realtime');
      setRoomCode(code);
      setWaitingForOpponent(true);
      setShowModeSelector(false);

      try {
        await services.multiplayer.joinRoom(code);
        console.log('✅ Successfully joined chess room');
        const color = services.multiplayer.getPlayerColor();
        if (color) {
          setPlayerColor(color);
        }
        setWaitingForOpponent(false);
        setOpponentConnected(true);
      } catch (error: any) {
        console.error('❌ Failed to join chess room:', error);
        NotificationService.getInstance().sendErrorNotification(
          `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
        );
        setShowModeSelector(true);
        setWaitingForOpponent(false);
        setRoomCode(null);
      }
    };

    return (
      <div className="chess-game-container">
        <UniversalGameModeSelector
          config={chessConfig}
          onStart={handleModeSelection}
          onCreateRoom={() => handleModeSelection({ mode: 'online', onlineAction: 'create' })}
          onJoinRoom={handleJoinPrivateGame}
          isLoading={false}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  console.log('ChessGame: Rendering game board, showModeSelector:', showModeSelector);

  const gameState = engine.getGameState();

  // Determine game status for the status bar
  const gameStatusText = gameState.gameStatus.type === 'check' ? '⚠️ Check!'
    : gameState.gameStatus.type === 'checkmate' ? '👑 Checkmate!'
    : gameState.gameStatus.type === 'stalemate' ? '🤝 Stalemate'
    : gameState.gameStatus.type === 'draw' ? '🤝 Draw'
    : undefined;

  const gameStatusType = gameState.gameStatus.type === 'check' ? 'check' as const
    : (gameState.gameStatus.type === 'checkmate' || gameState.gameStatus.type === 'stalemate' || gameState.gameStatus.type === 'draw') ? 'ended' as const
    : 'active' as const;

  // Determine game-over state
  const isGameOver = gameState.gameStatus.type === 'checkmate' || gameState.gameStatus.type === 'stalemate' || gameState.gameStatus.type === 'draw';
  const gameOverResult = gameState.gameStatus.type === 'checkmate'
    ? (gameState.gameStatus.winner === playerColor ? 'win' as const : 'loss' as const)
    : 'draw' as const;
  const gameOverTitle = gameState.gameStatus.type === 'checkmate' ? 'Checkmate!'
    : gameState.gameStatus.type === 'stalemate' ? 'Stalemate!'
    : 'Draw!';
  const gameOverSubtitle = gameState.gameStatus.type === 'checkmate'
    ? `${gameState.gameStatus.winner === 'white' ? 'White' : 'Black'} wins by checkmate`
    : gameState.gameStatus.type === 'stalemate' ? 'Game ended in stalemate'
    : 'Game ended in a draw';
  const gameOverIcon = gameState.gameStatus.type === 'checkmate'
    ? (gameState.gameStatus.winner === playerColor ? '🏆' : '😞')
    : '🤝';

  // Build the UniversalGameLayout config
  const layoutConfig: UniversalGameLayoutConfig = (() => {
    // Left panels
    const leftPanels = [
      {
        id: 'history',
        title: 'Move History',
        icon: '📜',
        content: (
          <MoveHistory
            moves={gameState.moveHistory}
            currentMoveIndex={gameState.moveHistory.length - 1}
          />
        ),
      },
      {
        id: 'captured',
        title: 'Captured Pieces',
        icon: '♟️',
        content: (
          <CapturedPieces
            capturedPieces={gameState.capturedPieces}
            orientation={playerColor}
          />
        ),
      },
      {
        id: 'appearance',
        title: 'Appearance',
        icon: '🎨',
        content: (
          <div className="chess-appearance-panel">
            <label className="chess-dark-mode-toggle">
              <span>Dark Mode</span>
              <input
                type="checkbox"
                checked={document.documentElement.getAttribute('data-theme') === 'dark'}
                onChange={() => {
                  const current = document.documentElement.getAttribute('data-theme');
                  document.documentElement.setAttribute('data-theme', current === 'dark' ? 'light' : 'dark');
                }}
              />
            </label>
          </div>
        ),
        defaultFolded: true,
        pinToBottom: true,
      },
    ];

    // Right panels
    const rightPanels = gameMode === 'singleplayer' ? [
      {
        id: 'coach',
        title: 'AI Coach',
        icon: '🤖',
        content: (
          <GameCoach
            coach={coachService}
            gameContext={{
              gameName: 'Chess',
              gameState: `Move ${Math.floor(gameState.moveHistory.length / 2) + 1}. ${gameState.currentPlayer}'s turn. ${gameState.gameStatus.type === 'check' ? gameState.currentPlayer + ' is in check!' : ''}`,
              playerContext: `You are playing as ${playerColor}. AI difficulty: ${aiDifficulty}.`,
              lastMove: gameState.moveHistory.length > 0 ? gameState.moveHistory[gameState.moveHistory.length - 1]?.notation : undefined,
            }}
            isPlayerTurn={engine.getCurrentPlayer() === playerColor}
            quickQuestions={['What should I do next?', 'Explain this position', 'What are my best moves?', 'Am I in danger?']}
            accentColor="#4CAF50"
            className="chess-coach-position"
          />
        ),
      },
    ] : [];

    // Status bar
    const statusBar = {
      turnIndicator: (
        <span>
          {gameState.currentPlayer === 'white' ? '⚪' : '⚫'}{' '}
          {gameState.currentPlayer.charAt(0).toUpperCase() + gameState.currentPlayer.slice(1)}'s turn
          {(gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased') && services.multiplayer.isMyTurn(gameState.currentPlayer) && (
            <span className="your-turn-badge" style={{ marginLeft: '0.5rem' }}>Your Turn!</span>
          )}
        </span>
      ),
      connectionStatus: (gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased')
        ? connectionStatus
        : undefined,
      roomCode: roomCode || undefined,
      gameStatus: gameStatusText,
      gameStatusType: gameStatusType,
      aiThinking: isAIThinking,
    };

    // Actions
    const actions = [
      ...(gameMode === 'singleplayer' && engine.canUndo() ? [{
        id: 'undo',
        label: 'Undo',
        icon: '↩️',
        onClick: handleUndo,
        variant: 'secondary' as const,
      }] : []),
      {
        id: 'new-game',
        label: 'New Game',
        icon: '🔄',
        onClick: handleNewGame,
        variant: 'primary' as const,
      },
      ...((gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased') ? [{
        id: 'resign',
        label: 'Resign',
        icon: '🏳️',
        onClick: () => services.multiplayer.resign(),
        disabled: gameState.gameStatus.type !== 'active' && gameState.gameStatus.type !== 'check',
        variant: 'danger' as const,
      }] : []),
      ...(onExit ? [{
        id: 'exit',
        label: 'Exit',
        icon: '🚪',
        onClick: onExit,
        variant: 'secondary' as const,
      }] : []),
    ];

    // Secondary actions (AI speed slider for single-player)
    const secondaryActions = gameMode === 'singleplayer' ? [
      <div key="ai-speed" className="ai-speed-control">
        <label htmlFor="ai-speed">AI Speed:</label>
        <select
          id="ai-speed"
          value={aiMoveDelay}
          onChange={(e) => setAiMoveDelay(Number(e.target.value))}
          aria-label="AI move speed"
        >
          <option value={500}>Fast (0.5s)</option>
          <option value={1000}>Normal (1s)</option>
          <option value={1500}>Slow (1.5s)</option>
          <option value={2000}>Very Slow (2s)</option>
        </select>
      </div>
    ] : undefined;

    // Game over config
    const gameOver = isGameOver ? {
      show: true,
      result: gameOverResult,
      title: gameOverTitle,
      subtitle: gameOverSubtitle,
      icon: gameOverIcon,
      onPlayAgain: handleNewGame,
      onExit: onExit || handleNewGame,
    } : undefined;

    // Share config for multiplayer
    const onShare = (gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased') && roomCode
      ? () => { /* Share handled by UniversalGameLayout */ }
      : undefined;

    return {
      gameName: 'Chess',
      board: (
        <ChessBoard
          engine={engine}
          orientation={playerColor}
          interactive={
            gameMode === 'singleplayer'
              ? engine.getCurrentPlayer() === playerColor
              : gameMode === 'multiplayer-realtime' || gameMode === 'multiplayer-turnbased'
                ? services.multiplayer.isMyTurn(engine.getCurrentPlayer())
                : true
          }
          showLegalMoves={true}
          showLastMove={true}
          showCoordinates={true}
          onMove={handleMoveMade}
          moveCount={moveCount}
        />
      ),
      leftPanels,
      rightPanels,
      statusBar,
      actions,
      secondaryActions,
      gameOver,
      onShare,
      roomCode: roomCode || undefined,
      isLoading: waitingForOpponent,
    };
  })();

  // Waiting for opponent - show RoomCodeDisplay within the layout
  if (waitingForOpponent && roomCode) {
    return (
      <div className="chess-game-container">
        <RoomCodeDisplay
          roomCode={roomCode}
          gameType="chess"
          playerRole={playerColor === 'white' ? 'White' : 'Black'}
          playerName={isAuthenticated && user?.name ? user.name : 'Guest'}
          maxPlayers={2}
          onCancel={() => {
            services.multiplayer.leaveGame();
            setWaitingForOpponent(false);
            setRoomCode(null);
            setShowModeSelector(true);
            setGameMode(null);
          }}
          showCancelButton={true}
        />
      </div>
    );
  }

  // Matchmaking UI for public games
  if (waitingForOpponent && !roomCode) {
    return (
      <div className="chess-game-container">
        <div className="waiting-for-opponent">
          <div className="room-code-display">
            <h2>Finding Opponent...</h2>
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Searching for an available player...</p>
            </div>
            <button
              onClick={() => {
                services.multiplayer.leaveGame();
                setWaitingForOpponent(false);
                setShowModeSelector(true);
              }}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chess-game-container">
      <UniversalGameLayout config={layoutConfig} />

      {showPromotion && (
        <PromotionDialog
          color={engine.getCurrentPlayer()}
          onSelect={handlePromotionSelect}
          isOpen={showPromotion}
        />
      )}
    </div>
  );
};
